import { BRAND } from './brand'
import { lariInWords } from './money-words'
import { esc, m2 } from './print'

/**
 * The shop's own subscription to MartePOS (097) — where to pay, how much, until
 * when, and the paper a company's accountant asks for.
 *
 * Shops pay by bank transfer. The requisites live in `platform_billing` (one
 * row the platform admin edits in /platform), never in this code, and every
 * shop has a number so the transfer's purpose line says who paid: MP-0007.
 * Everything here is pure and tested; the database computes the amount again
 * when the owner says „I transferred" (`claim_payment`) and a platform admin
 * confirms it (`record_platform_payment`) — these functions only mirror them
 * for the screen.
 */

export type BillingAccount = { bank: string; iban: string }

export type PlatformBilling = {
  recipient: string
  recipient_id: string
  accounts: BillingAccount[]
  price_month: number
  price_year: number
  note: string
}

/** What the subscription screens need to know about the shop. */
export type SubscriptionOrg = {
  plan: 'trial' | 'pro' | 'enterprise'
  trial_ends_at: string | null
  paid_until: string | null
}

const DAY = 86_400_000
/** Writes stay open this long after a trial's last day (current_org_active). */
export const TRIAL_GRACE_DAYS = 3

/** MP-0007 — the shop's number on a bank statement. */
export const payCode = (n: number | null | undefined) => (n ? `MP-${String(n).padStart(4, '0')}` : '')

/** The purpose line the shop types into its bank app. */
export const payPurpose = (n: number | null | undefined) => `${BRAND} ${payCode(n)}`.trim()

/** The price of a period — mirrors claim_payment: a negotiated month × 10 for a year. */
export function priceFor(b: Pick<PlatformBilling, 'price_month' | 'price_year'>, custom: number | null | undefined, months: 1 | 12): number {
  if (custom && custom > 0) return months === 12 ? custom * 10 : custom
  return months === 12 ? b.price_year : b.price_month
}

export const compactIban = (s: string) => s.replace(/\s+/g, '').toUpperCase()

/** GE69 TB72 1864 5064 3000 90 — how a bank app shows it, easier to compare. */
export const groupIban = (s: string) => compactIban(s).replace(/(.{4})(?=.)/g, '$1 ')

/**
 * ISO 13616 check: country, two check digits, then the account; the mod-97 of
 * the rearranged number must be 1. A Georgian IBAN is 22 characters.
 */
export function isValidIban(raw: string): boolean {
  const s = compactIban(raw)
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(s)) return false
  if (s.startsWith('GE') && s.length !== 22) return false
  const moved = s.slice(4) + s.slice(0, 4)
  let rem = 0
  for (const ch of moved) {
    const v = ch >= 'A' && ch <= 'Z' ? String(ch.charCodeAt(0) - 55) : ch
    for (const d of v) rem = (rem * 10 + Number(d)) % 97
  }
  return rem === 1
}

export type SubState =
  | { kind: 'trial'; daysLeft: number }
  | { kind: 'trial-grace'; lockInDays: number }
  | { kind: 'trial-locked' }
  | { kind: 'paid'; until: string; daysLeft: number }
  | { kind: 'due-soon'; until: string; daysLeft: number }
  | { kind: 'overdue'; until: string; daysOver: number }
  /** enterprise/pro with no date — a hand-arranged shop; nothing to show */
  | { kind: 'none' }

/** Where the shop stands. Due-soon is the last seven days, as in the reminder mail. */
export function subscriptionState(org: SubscriptionOrg, now = Date.now()): SubState {
  if (org.plan === 'trial') {
    if (!org.trial_ends_at) return { kind: 'trial', daysLeft: 14 }
    const ends = new Date(org.trial_ends_at).getTime()
    const daysLeft = Math.ceil((ends - now) / DAY)
    if (daysLeft > 0) return { kind: 'trial', daysLeft }
    const lockInDays = Math.ceil((ends + TRIAL_GRACE_DAYS * DAY - now) / DAY)
    return lockInDays > 0 ? { kind: 'trial-grace', lockInDays } : { kind: 'trial-locked' }
  }
  if (!org.paid_until) return { kind: 'none' }
  const days = Math.ceil((new Date(org.paid_until).getTime() - now) / DAY)
  if (days < 0) return { kind: 'overdue', until: org.paid_until, daysOver: -days }
  if (days <= 7) return { kind: 'due-soon', until: org.paid_until, daysLeft: days }
  return { kind: 'paid', until: org.paid_until, daysLeft: days }
}

/** Postgres month arithmetic: Jan 31 + 1 month = Feb 28 (the day clamps). */
export function addMonths(d: Date, months: number): Date {
  const out = new Date(d.getTime())
  const day = out.getDate()
  out.setDate(1)
  out.setMonth(out.getMonth() + months)
  const last = new Date(out.getFullYear(), out.getMonth() + 1, 0).getDate()
  out.setDate(Math.min(day, last))
  return out
}

/**
 * The period a payment made now buys — mirrors record_platform_payment: it
 * starts at the later of today, the paid-until date and (on a trial) the
 * trial's last day, so paying early never loses days.
 */
export function periodFor(org: SubscriptionOrg, months: number, now = new Date()): { from: Date; to: Date } {
  let from = now.getTime()
  if (org.paid_until) from = Math.max(from, new Date(org.paid_until).getTime())
  if (org.plan === 'trial' && org.trial_ends_at) from = Math.max(from, new Date(org.trial_ends_at).getTime())
  const start = new Date(from)
  return { from: start, to: addMonths(start, months) }
}

export const geDate = (d: Date | string) => {
  const x = typeof d === 'string' ? new Date(d) : d
  return `${String(x.getDate()).padStart(2, '0')}.${String(x.getMonth() + 1).padStart(2, '0')}.${x.getFullYear()}`
}

/** 18.09 22:10 — ours, not the browser's locale (a Chrome without ka-GE data prints „09/18, 10:06 PM"). */
export const geDateTime = (d: Date | string) => {
  const x = typeof d === 'string' ? new Date(d) : d
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(x.getDate())}.${p(x.getMonth() + 1)} ${p(x.getHours())}:${p(x.getMinutes())}`
}

// ---------------------------------------------------------------- the invoice

export type SubInvoiceModel = {
  number: string
  date: string
  seller: { name: string; id: string; accounts: BillingAccount[] }
  buyer: { name: string; tin: string; address: string }
  description: string
  period: string
  amount: number
  words: string
  purpose: string
}

/**
 * The paper for one period: from MartePOS's owner to the shop, with the
 * accounts and the purpose line. Numbered by shop and month (MP-0007-2609) —
 * printing it twice in a month gives the same paper, not a new debt.
 */
export function buildSubInvoice(
  billing: PlatformBilling,
  org: SubscriptionOrg & { billing_no: number | null; custom_price_month?: number | null; name: string },
  shop: { companyName: string; companyId: string; address: string },
  months: 1 | 12,
  now = new Date(),
): SubInvoiceModel {
  const amount = priceFor(billing, org.custom_price_month, months)
  const { from, to } = periodFor(org, months, now)
  const last = new Date(to.getTime() - DAY)
  const yy = String(now.getFullYear()).slice(2)
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  return {
    number: `${payCode(org.billing_no) || 'MP'}-${yy}${mm}`,
    date: geDate(now),
    seller: { name: billing.recipient || BRAND, id: billing.recipient_id, accounts: billing.accounts },
    buyer: { name: shop.companyName || org.name, tin: shop.companyId, address: shop.address },
    description: `${BRAND} — პროგრამით სარგებლობის სააბონენტო მომსახურება, ${months === 12 ? '12 თვე' : '1 თვე'}`,
    period: `${geDate(from)} – ${geDate(last)}`,
    amount,
    words: lariInWords(amount),
    purpose: payPurpose(org.billing_no),
  }
}

export function subInvoiceHtml(m: SubInvoiceModel): string {
  const accounts = m.seller.accounts
    .map((a) => `<tr><td>${esc(a.bank)}</td><td class="mono">${esc(groupIban(a.iban))}</td></tr>`)
    .join('')
  return `<!doctype html><html lang="ka"><head><meta charset="utf-8"><title>ინვოისი ${esc(m.number)}</title>
<style>
  @page { size: A4 portrait; margin: 14mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Noto Sans Georgian', 'Sylfaen', 'DejaVu Sans', sans-serif; color: #111; margin: 0; font-size: 12px; }
  .head { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #111; padding-bottom: 10px; }
  .head h1 { margin: 0; font-size: 26px; letter-spacing: 1px; }
  .head .no { text-align: right; } .head .no b { font-size: 16px; }
  .parties { display: flex; gap: 14px; margin: 16px 0; }
  .party { flex: 1; border: 1px solid #bbb; border-radius: 6px; padding: 10px 12px; }
  .party .cap { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #666; margin-bottom: 4px; }
  .party .nm { font-size: 14px; font-weight: 700; } .party div { margin-top: 2px; }
  table.lines { width: 100%; border-collapse: collapse; }
  table.lines th, table.lines td { border-bottom: 1px solid #ddd; padding: 8px 6px; text-align: left; }
  table.lines th { background: #f2f2f2; font-weight: 700; font-size: 11px; }
  table.lines td.r, table.lines th.r { text-align: right; white-space: nowrap; }
  .total { display: flex; justify-content: flex-end; margin-top: 10px; font-size: 15px; font-weight: 800; gap: 24px; border-top: 2px solid #111; padding-top: 6px; }
  .words { margin-top: 8px; } .words b { font-weight: 700; }
  .bank { margin-top: 18px; padding: 12px 14px; border: 1px dashed #999; border-radius: 6px; }
  .bank table { border-collapse: collapse; margin-top: 6px; } .bank td { padding: 3px 14px 3px 0; }
  .mono { font-family: 'DejaVu Sans Mono', Consolas, monospace; letter-spacing: 0.5px; }
  .purpose { margin-top: 8px; } .purpose b { font-size: 14px; }
</style></head><body>
<div class="head"><h1>ინვოისი</h1><div class="no"><b>${esc(m.number)}</b><br>თარიღი: ${esc(m.date)}</div></div>
<div class="parties">
  <div class="party"><div class="cap">მომწოდებელი</div><div class="nm">${esc(m.seller.name)}</div>${m.seller.id ? `<div>საიდ. № ${esc(m.seller.id)}</div>` : ''}<div>${esc(BRAND)}</div></div>
  <div class="party"><div class="cap">მიმღები</div><div class="nm">${esc(m.buyer.name)}</div>${m.buyer.tin ? `<div>ს/კ ${esc(m.buyer.tin)}</div>` : ''}${m.buyer.address ? `<div>${esc(m.buyer.address)}</div>` : ''}</div>
</div>
<table class="lines"><thead><tr><th>დასახელება</th><th>პერიოდი</th><th class="r">თანხა ₾</th></tr></thead>
<tbody><tr><td>${esc(m.description)}</td><td>${esc(m.period)}</td><td class="r">${m2(m.amount)}</td></tr></tbody></table>
<div class="total"><span>სულ გადასახდელი</span><span>₾${m2(m.amount)}</span></div>
<div class="words">თანხა სიტყვიერად: <b>${esc(m.words)}</b></div>
<div class="bank"><b>გადასახდელი ანგარიში</b> — მიმღები: ${esc(m.seller.name)}
<table>${accounts}</table>
<div class="purpose">დანიშნულება: <b class="mono">${esc(m.purpose)}</b></div></div>
</body></html>`
}
